import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function GuardianConsentPage() {
  const { token } = useParams();
  const { refreshMe } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardianName, setGuardianName] = useState("");
  const [granting, setGranting] = useState(false);
  const [granted, setGranted] = useState(false);
  const [dashboardLink, setDashboardLink] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/guardian-consent/${token}`);
        setData(data);
      } catch (e) {
        setError(extractError(e, "Could not load consent request"));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleGrant = async (e) => {
    e.preventDefault();
    setGranting(true);
    setError("");
    try {
      const { data: grant } = await api.post(`/auth/grant-guardian-consent`, {
        token,
        guardian_name: guardianName,
      });
      setDashboardLink(grant?.dashboard_link || null);
      // If the minor is logged in in this browser, refresh their /me so the
      // pricing notice + dashboard banner clear without a manual reload.
      try { await refreshMe(); } catch (err) { console.warn("refreshMe failed after consent grant:", err); }
      setGranted(true);
    } catch (e) {
      setError(extractError(e, "Could not record consent"));
    } finally {
      setGranting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-[color:var(--text-muted)]" data-testid="consent-loading">
        Loading consent request…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="consent-error">
        <h1 className="font-serif text-2xl mb-1">Consent link unavailable</h1>
        <p className="text-sm text-[color:var(--counter-error)]">{error || "This link is invalid or expired."}</p>
        <Link to="/" className="mt-4 inline-block text-[color:var(--participant-a)] underline">
          Back to home
        </Link>
      </div>
    );
  }

  if (granted || data.used) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 fade-in" data-testid="consent-granted">
        <h1 className="font-serif text-3xl">Thank you</h1>
        <p className="text-sm text-[color:var(--text-muted)] mt-2">
          Guardian consent has been recorded for{" "}
          <b className="text-[color:var(--text-primary)]">{data.minor.name}</b>{" "}
          ({data.minor.email}). They can now upgrade their plan and involve a
          professional mediator if they wish.
        </p>
        {dashboardLink && (
          <div
            className="mt-5 rounded-xl border border-[color:var(--line)] bg-[color:var(--surface-sage)] p-4"
            data-testid="guardian-dashboard-link-block"
          >
            <div className="text-sm font-medium">Bookmark your guardian dashboard</div>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              From this private link you can see every action {data.minor.name}{" "}
              takes on FairResolver and revoke consent at any time.
            </p>
            <a
              href={dashboardLink}
              className="mt-3 inline-block px-4 py-2 rounded-full bg-[color:var(--participant-a)] text-white text-sm font-semibold hover:opacity-90"
              data-testid="open-guardian-dashboard"
            >
              Open guardian dashboard
            </a>
            <div className="mt-2 text-[10px] text-[color:var(--text-muted)] break-all">{dashboardLink}</div>
          </div>
        )}
        <Link to="/" className="mt-6 inline-block text-[color:var(--participant-a)] underline">
          Back to home
        </Link>
      </div>
    );
  }

  if (data.expired) {
    return (
      <div className="max-w-md mx-auto px-6 py-16" data-testid="consent-expired">
        <h1 className="font-serif text-2xl mb-1">This consent link has expired</h1>
        <p className="text-sm text-[color:var(--text-muted)]">
          Please ask {data.minor.name} to request a new consent link from their
          dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-12 fade-in" data-testid="consent-page">
      <div className="bg-white rounded-2xl border border-[color:var(--line)] p-7 shadow-sm">
        <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
          Guardian consent
        </p>
        <h1 className="font-serif text-3xl mt-1 mb-3">
          Approve participation for {data.minor.name}
        </h1>
        <p className="text-sm text-[color:var(--text-muted)] leading-relaxed">
          <b className="text-[color:var(--text-primary)]">{data.minor.name}</b>{" "}
          (age {data.minor.age}, {data.minor.email}) is using FairResolver, a
          structured conflict-resolution app. They've asked you, as their
          guardian, to consent so they can upgrade their plan or involve a
          professional mediator if needed.
        </p>
        <ul className="text-xs text-[color:var(--text-muted)] mt-3 space-y-1 list-disc pl-5">
          <li>FairResolver enforces respectful turn-based conversations.</li>
          <li>The Free tier (150-word turns) does not require your consent.</li>
          <li>This consent covers paid upgrades and professional mediator involvement.</li>
          <li>You may revoke consent at any time by replying to support.</li>
        </ul>

        <form onSubmit={handleGrant} className="mt-6 space-y-3">
          <div>
            <label className="text-sm text-[color:var(--text-muted)] mb-1 block">
              Your full name (guardian)
            </label>
            <input
              required
              minLength={2}
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-a)]/30"
              data-testid="guardian-name-input"
            />
          </div>
          {error && (
            <div className="text-sm text-[color:var(--counter-error)]" data-testid="consent-error-msg">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={granting}
            className="w-full px-4 py-3 rounded-full bg-[color:var(--participant-a)] text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
            data-testid="guardian-grant-submit"
          >
            {granting ? "Recording consent…" : "I confirm consent"}
          </button>
        </form>
      </div>
    </div>
  );
}
